import React, { useState, useEffect } from "react";
import { base44 } from "@/api/base44Client";
import { Loader2, CheckCircle2, ArrowRight } from "lucide-react";
import { pushEvent } from "@/lib/gtm";

const inputCls = "w-full bg-transparent border border-[#A8ADB4]/50 focus:border-[#E4001C] outline-none text-white px-4 py-3 text-sm transition-colors placeholder:text-[#A8ADB4]/60";
const labelCls = "block font-tech font-semibold uppercase tracking-widest text-xs text-[#A8ADB4] mb-2";

export default function RFQForm() {
  const urlParams = new URLSearchParams(window.location.search);
  const [products, setProducts] = useState([]);
  const [form, setForm] = useState({
    name: "", company: "", email: "", phone: "", location: "",
    product_interest: urlParams.get("product") || "", quantity: "", message: "",
    source: "",
  });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [submitError, setSubmitError] = useState("");

  useEffect(() => {
    base44.entities.Product.list("-updated_date", 500)
      .then((data) => {
        const activeProducts = data.filter((p) => p.status === "active");
        setProducts(activeProducts);
      })
      .catch((err) => console.error("Failed to load products for RFQ form:", err));
  }, []);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errs = {};
    if (!form.name.trim()) errs.name = "Full name is required";
    if (!form.email.trim() || !/^\S+@\S+\.\S+$/.test(form.email)) errs.email = "Valid email is required";
    if (!form.phone.trim()) errs.phone = "Phone number is required";
    if (!form.source) errs.source = "Please select where you heard from us";
    setErrors(errs);
    if (Object.keys(errs).length) {
      pushEvent("contact_form_error", { missing_fields: Object.keys(errs).join(",") });
      return;
    }

    setSubmitting(true);
    setSubmitError("");
    try {
      const res = await base44.functions.invoke("createInquiry", form);
      base44.analytics.track({
        eventName: "inquiry_form_submitted",
        properties: {
          product_interest: form.product_interest || "none",
          source: form.source,
        },
      });
      if (typeof window.gtag === "function") {
        window.gtag("event", "conversion", {
          send_to: "AW-18352855047/UXdOCMiGhdscEIewqa9E",
          value: 1.0,
          currency: "PHP",
        });
      }
      pushEvent("contact_form_submit", {
        form_name: "rfq_terminal",
        page_location: window.location.pathname,
        product_interest: form.product_interest || "none",
        lead_source: form.source,
        inquiry_number: res.data.inquiry_number,
      });
      setResult(res.data.inquiry_number);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch {
      pushEvent("contact_form_error", { reason: "submit_failed" });
      setSubmitError("Something went wrong sending your inquiry. Please try again or contact us directly.");
    }
    setSubmitting(false);
  };

  if (result) {
    return (
      <div className="bg-[#171717] p-10 text-center border-t-4 border-[#E4001C]">
        <CheckCircle2 className="w-14 h-14 text-[#E4001C] mx-auto mb-6" />
        <h2 className="font-heading font-extrabold uppercase text-3xl text-white tracking-wide mb-3">Inquiry Received</h2>
        <p className="text-[#A8ADB4] text-sm mb-8">Your reference number — quote this in all follow-ups:</p>
        <p className="font-heading font-extrabold text-5xl md:text-6xl text-[#E4001C] tracking-widest mb-8">{result}</p>
        <p className="text-[#A8ADB4] text-sm leading-relaxed max-w-md mx-auto">
          Our engineering team will review your requirements and respond within 1–2 business days.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} noValidate className="bg-[#171717] p-8 md:p-10 border-t-4 border-[#E4001C] space-y-6 animate-in fade-in">
      <h2 className="font-heading font-extrabold uppercase text-3xl text-white tracking-wide">RFQ Terminal</h2>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
        <div>
          <label className={labelCls}>Full Name *</label>
          <input className={inputCls} value={form.name} onChange={set("name")} placeholder="Juan Dela Cruz" />
          {errors.name && <p className="text-[#E4001C] text-xs mt-1">{errors.name}</p>}
        </div>
        <div>
          <label className={labelCls}>Company Name</label>
          <input className={inputCls} value={form.company} onChange={set("company")} placeholder="ABC Construction Corp." />
        </div>
        <div>
          <label className={labelCls}>Email *</label>
          <input type="email" className={inputCls} value={form.email} onChange={set("email")} placeholder="you@company.com" />
          {errors.email && <p className="text-[#E4001C] text-xs mt-1">{errors.email}</p>}
        </div>
        <div>
          <label className={labelCls}>Phone Number *</label>
          <input type="tel" className={inputCls} value={form.phone} onChange={set("phone")} placeholder="+63 9XX XXX XXXX" />
          {errors.phone && <p className="text-[#E4001C] text-xs mt-1">{errors.phone}</p>}
        </div>
        <div>
          <label className={labelCls}>Project Location</label>
          <input className={inputCls} value={form.location} onChange={set("location")} placeholder="City / Province" />
        </div>
        <div>
          <label className={labelCls}>Product / System Interested In</label>
          <select className={`${inputCls} bg-[#171717] text-white`} value={form.product_interest} onChange={set("product_interest")}>
            <option value="" style={{ backgroundColor: "#171717", color: "#fff" }} className="bg-[#171717] text-white">Select a system…</option>
            {form.product_interest && !products.some(p => p.name === form.product_interest) && (
              <option value={form.product_interest} style={{ backgroundColor: "#171717", color: "#fff" }} className="bg-[#171717] text-white">{form.product_interest}</option>
            )}
            {products.map((p) => (
              <option key={p.id} value={p.name} style={{ backgroundColor: "#171717", color: "#fff" }} className="bg-[#171717] text-white">{p.name}</option>
            ))}
            <option value="Not Sure / Need Recommendation" style={{ backgroundColor: "#171717", color: "#fff" }} className="bg-[#171717] text-white">Not Sure / Need Recommendation</option>
          </select>
        </div>
        <div>
          <label className={labelCls}>Where did you hear from us? *</label>
          <select className={`${inputCls} bg-[#171717] text-white`} value={form.source} onChange={set("source")}>
            <option value="" style={{ backgroundColor: "#171717", color: "#fff" }} className="bg-[#171717] text-white">Select an option…</option>
            <option value="Website" style={{ backgroundColor: "#171717", color: "#fff" }} className="bg-[#171717] text-white">Website</option>
            <option value="Google Search" style={{ backgroundColor: "#171717", color: "#fff" }} className="bg-[#171717] text-white">Google Search</option>
            <option value="Facebook" style={{ backgroundColor: "#171717", color: "#fff" }} className="bg-[#171717] text-white">Facebook</option>
          </select>
          {errors.source && <p className="text-[#E4001C] text-xs mt-1">{errors.source}</p>}
        </div>
      </div>
      <div>
        <label className={labelCls}>Estimated Quantity / Project Scope</label>
        <input className={inputCls} value={form.quantity} onChange={set("quantity")} placeholder="e.g. 120 linear meters, 3-storey commercial building" />
      </div>
      <div>
        <label className={labelCls}>Message / Additional Details</label>
        <textarea rows={4} className={inputCls} value={form.message} onChange={set("message")} placeholder="Tell us about your project requirements…" />
      </div>
      {submitError && <p className="text-[#E4001C] text-sm">{submitError}</p>}
      <button type="submit" disabled={submitting}
        className="w-full sm:w-auto inline-flex items-center justify-center gap-3 bg-[#E4001C] hover:bg-[#c00018] disabled:opacity-60 text-white font-tech font-semibold uppercase tracking-widest px-10 py-4 transition-all hover:gap-4">
        {submitting ? <><Loader2 className="w-5 h-5 animate-spin" /> Processing…</> : <>Submit RFQ <ArrowRight className="w-5 h-5" /></>}
      </button>
    </form>
  );
}